import React, { useState, useCallback } from "react";
import ReactDOM from "react-dom";
import Cropper, { Area } from "react-easy-crop";
import "./ImageCropModal.css";

interface ImageCropModalProps {
  /** The image source URL (object URL or data URL) to crop */
  imageSrc: string;
  /** Fixed aspect ratio for the crop area (e.g. 4/3, 1/1) */
  aspectRatio?: number;
  /** Called with the cropped image blob when user confirms */
  onCropComplete: (croppedBlob: Blob) => void;
  /** Called when the user cancels and wants to discard this image capture entirely */
  onCancel: () => void;
}

/**
 * A full-screen modal that lets the user crop an image with a fixed aspect ratio.
 * Uses react-easy-crop under the hood. The area outside the crop zone is darkened.
 */
const ImageCropModal: React.FC<ImageCropModalProps> = ({
  imageSrc,
  aspectRatio = 1 / 1,
  onCropComplete,
  onCancel,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const handleCropComplete = useCallback(
    (_croppedArea: Area, croppedAreaPixels: Area) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    []
  );

  /** Extracts the cropped region from the original image as a Blob */
  const getCroppedBlob = useCallback(async (): Promise<Blob> => {
    if (!croppedAreaPixels) throw new Error("No crop area selected");

    const image = new Image();
    image.crossOrigin = "anonymous";

    return new Promise((resolve, reject) => {
      image.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = croppedAreaPixels.width;
        canvas.height = croppedAreaPixels.height;

        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Could not get canvas context"));
          return;
        }

        // 1. Draw blurred image as background (cover-scaled, like Instagram/Google Photos)
        const bgScale = Math.max(
          canvas.width / image.naturalWidth,
          canvas.height / image.naturalHeight
        );
        const bgW = image.naturalWidth * bgScale;
        const bgH = image.naturalHeight * bgScale;
        const bgX = (canvas.width - bgW) / 2;
        const bgY = (canvas.height - bgH) / 2;
        // Overshoot by blur radius so edges don't show transparent halos
        ctx.filter = "blur(40px)";
        ctx.drawImage(image, bgX - 80, bgY - 80, bgW + 160, bgH + 160);
        ctx.filter = "none";

        // 2. Draw the actual cropped image on top (sharp)
        const dx = -croppedAreaPixels.x;
        const dy = -croppedAreaPixels.y;

        ctx.drawImage(image, dx, dy);

        canvas.toBlob(
          (blob) => {
            if (blob) resolve(blob);
            else reject(new Error("Canvas toBlob failed"));
          },
          "image/jpeg",
          0.92
        );
      };

      image.onerror = () => reject(new Error("Failed to load image for crop"));
      image.src = imageSrc;
    });
  }, [imageSrc, croppedAreaPixels]);

  const handleConfirm = async () => {
    try {
      const blob = await getCroppedBlob();
      onCropComplete(blob);
    } catch (err) {
      console.error("Crop failed:", err);
    }
  };

  return ReactDOM.createPortal(
    <div className="crop-modal-overlay">
      <div className="crop-modal-container">
        {/* Crop area */}
        <div className="crop-modal-cropper">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            minZoom={0.1} // Allow zooming out significantly to fit any image
            aspect={aspectRatio}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={handleCropComplete}
            showGrid={true}
            restrictPosition={false} // Allow image to be smaller than crop area
          />
        </div>

        {/* Controls */}
        <div className="crop-modal-controls">
          {/* Zoom slider */}
          <div className="crop-modal-zoom">
            <label className="crop-modal-zoom-label">Zoom</label>
            <input
              type="range"
              min={0.1}
              max={3}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="crop-modal-zoom-slider"
            />
          </div>

          {/* Action buttons */}
          <div className="crop-modal-actions">
            <button
              type="button"
              className="crop-modal-btn crop-modal-btn-cancel"
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              type="button"
              className="crop-modal-btn crop-modal-btn-confirm"
              onClick={handleConfirm}
            >
              Crop & Save
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ImageCropModal;
